/**
 * services/sync/OperationBuilder.ts
 * Centralized diff-based operation builder for sync
 *
 * Provides utilities for generating sync operations by comparing
 * before and after states. Supports dot-notation paths for nested
 * field tracking, enabling field-level change detection.
 *
 * Key features:
 * - Diff-based: Only logs changed fields (fewer ops than logging everything)
 * - Dot-notation: Supports nested paths like `trading.strategy.description`
 * - Partial updates: Missing keys in afterState are NOT treated as clears
 * - Arrays atomic: Array changes produce single op on array field
 */

import type { LogBatchOperationParams } from './SyncOperationService';
import type { SyncTableName } from '@/constants/syncTables';

/**
 * Fields excluded from op generation.
 * These are system metadata fields present on all synced tables.
 */
export const EXCLUDED_FIELDS = [
  'id',
  'createdAt',
  'updatedAt',
  'deletedAt',
] as const;

/**
 * Type for the flattened path-value map produced by flattenToDotNotation.
 */
export type FlattenedPaths = Record<string, unknown>;

/**
 * Check if a value is a plain object (not Date, Map, class instance, etc.)
 *
 * Only plain objects should be recursed into during flattening.
 * Non-plain objects (Date, Map, Set, class instances) are treated as leaf values.
 */
function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}

/**
 * Flatten a nested object to dot-notation paths.
 *
 * Recursively traverses the object and produces a flat map where keys
 * are dot-separated paths and values are the leaf values.
 *
 * Special handling:
 * - Arrays are treated atomically (not recursed into)
 * - null/undefined are leaf values (not recursed into)
 * - Empty objects produce no paths (they have no leaf values)
 *
 * @param obj - Object to flatten
 * @param prefix - Current path prefix (used in recursion)
 * @returns Flat map of dot-notation paths to values
 *
 * @example
 * flattenToDotNotation({ a: { b: 1 }, c: 2 })
 * // Returns: { 'a.b': 1, 'c': 2 }
 *
 * @example
 * flattenToDotNotation({ arr: [1, 2, 3] })
 * // Returns: { 'arr': [1, 2, 3] } // Array not recursed
 */
export function flattenToDotNotation(
  obj: Record<string, unknown>,
  prefix = ''
): FlattenedPaths {
  const result: FlattenedPaths = {};

  for (const [key, value] of Object.entries(obj)) {
    // Skip fields with '.' in key name - unsupported, could cause import corruption
    if (key.includes('.')) {
      console.warn(
        `[OperationBuilder] Skipping field with '.' in name: "${key}". ` +
        `Dot-in-key is unsupported and could corrupt data on import.`
      );
      continue;
    }

    const path = prefix ? `${prefix}.${key}` : key;

    // Check if value should be recursed into
    // - Arrays: atomic, don't recurse
    // - null/undefined: leaf values
    // - Plain objects only: recurse (not Date, Map, class instances)
    if (value === null || value === undefined) {
      // Leaf value
      result[path] = value;
    } else if (Array.isArray(value)) {
      // Arrays are atomic - don't recurse
      result[path] = value;
    } else if (isPlainObject(value)) {
      // Plain object - recurse
      const nested = flattenToDotNotation(value, path);
      Object.assign(result, nested);
    } else {
      // Primitive value OR non-plain object (Date, Map, etc.) - treat as leaf
      result[path] = value;
    }
  }

  return result;
}

/**
 * Normalize empty values to a consistent representation.
 *
 * Both undefined and null are treated as "empty" for comparison purposes.
 * This prevents generating ops for undefined→null transitions.
 *
 * @param value - Value to normalize
 * @returns null if value is null/undefined, otherwise unchanged
 */
export function normalizeEmpty(value: unknown): unknown {
  if (value === undefined || value === null) {
    return null;
  }
  return value;
}

/**
 * Check if a path should be excluded from op generation.
 *
 * A path is excluded if its root segment matches an excluded field.
 * For example, nested paths under `id` would be excluded because
 * `id` is in EXCLUDED_FIELDS.
 *
 * @param path - Dot-notation path to check
 * @returns true if path should be excluded
 */
function isExcludedPath(path: string): boolean {
  const rootSegment = path.split('.')[0];
  return EXCLUDED_FIELDS.includes(rootSegment as typeof EXCLUDED_FIELDS[number]);
}

/**
 * Build diff-based operations by comparing before and after states.
 *
 * Only generates ops for fields that changed. Uses partial update semantics:
 * paths present in beforeState but missing from afterState are NOT treated
 * as clears (the field is simply untouched).
 *
 * @param table - Target sync table name
 * @param recordId - Target record ID
 * @param beforeState - State before the change (from DB)
 * @param afterState - State after the change (write payload)
 * @returns Array of operations for changed fields
 *
 * @example
 * // Top-level change
 * buildDiffOps('trades', 'id-1', { name: 'Old' }, { name: 'New' })
 * // Returns: [{ op: 'set', field: 'name', value: 'New', ... }]
 *
 * @example
 * // Nested change
 * buildDiffOps('wizard_drafts', 'id-1',
 *   { trading: { strategy: { description: 'Old' } } },
 *   { trading: { strategy: { description: '' } } }
 * )
 * // Returns: [{ op: 'clear', field: 'trading.strategy.description', ... }]
 */
export function buildDiffOps(
  table: SyncTableName,
  recordId: string,
  beforeState: Record<string, unknown>,
  afterState: Record<string, unknown>
): LogBatchOperationParams[] {
  const ops: LogBatchOperationParams[] = [];

  // Flatten both states to dot-notation paths
  const beforeFlat = flattenToDotNotation(beforeState);
  const afterFlat = flattenToDotNotation(afterState);

  // Only iterate paths in afterState (partial update semantics)
  // Paths in beforeState but missing from afterState → no op
  for (const [path, afterValue] of Object.entries(afterFlat)) {
    // Skip excluded fields
    if (isExcludedPath(path)) {
      continue;
    }

    const beforeValue = beforeFlat[path];

    // Normalize empty values for comparison
    const normalizedBefore = normalizeEmpty(beforeValue);
    const normalizedAfter = normalizeEmpty(afterValue);

    // Special case: clearing a parent object that was nested in beforeState
    // e.g., before: { trading: { strategy: { name: 'X' } } } (flattens to trading.strategy.name)
    //       after: { trading: null } (flattens to trading: null)
    // The path 'trading' doesn't exist in beforeFlat, but trading.* paths do
    if (normalizedAfter === null && beforeValue === undefined) {
      // Check if any paths in beforeFlat start with this path as prefix
      const prefix = path + '.';
      const hasNestedPaths = Object.keys(beforeFlat).some(beforePath => beforePath.startsWith(prefix));
      if (hasNestedPaths) {
        // Clearing a subtree - generate clear op
        ops.push({
          table,
          recordId,
          op: 'clear',
          field: path,
        });
        continue;
      }
    }

    // Compare using JSON.stringify for deep equality
    const beforeJson = JSON.stringify(normalizedBefore);
    const afterJson = JSON.stringify(normalizedAfter);

    if (beforeJson === afterJson) {
      // No change
      continue;
    }

    // Value changed - generate appropriate op
    if (normalizedAfter === null) {
      // Value became empty → clear op
      ops.push({
        table,
        recordId,
        op: 'clear',
        field: path,
      });
    } else {
      // Value changed to non-empty → set op
      ops.push({
        table,
        recordId,
        op: 'set',
        field: path,
        value: afterValue,
      });
    }
  }

  // Detect removed nested fields: paths in beforeFlat but missing from afterFlat
  // Only generate clear ops when the parent top-level field is being updated
  // (i.e., it's in afterState and is a plain object - not null/primitive/array)
  for (const beforePath of Object.keys(beforeFlat)) {
    // Skip top-level paths (no dots) - they're handled by the first loop
    // This prevents false clears when primitive→object transitions occur
    // e.g., before: { x: 'primitive' }, after: { x: { nested: 'value' } }
    if (!beforePath.includes('.')) {
      continue;
    }

    // Skip if path exists in afterFlat (already handled above)
    if (Object.prototype.hasOwnProperty.call(afterFlat, beforePath)) {
      continue;
    }

    // Skip excluded paths
    if (isExcludedPath(beforePath)) {
      continue;
    }

    // Get root key (first segment before '.')
    const rootKey = beforePath.split('.')[0] as string;

    // Only generate clear if:
    // 1. The root key is in afterState (caller is updating this top-level field)
    // 2. The afterState[rootKey] is a plain object (not null/array/primitive)
    //    If it's null/primitive, the subtree clearing logic above already handles it
    if (!Object.prototype.hasOwnProperty.call(afterState, rootKey)) {
      // Root key not in afterState - this is a true partial update, don't clear
      continue;
    }

    const afterRootValue = afterState[rootKey];
    if (!isPlainObject(afterRootValue)) {
      // Parent is null/array/primitive - subtree clearing already handled above
      continue;
    }

    // This nested field was removed (existed in before, missing from after's plain object)
    ops.push({
      table,
      recordId,
      op: 'clear',
      field: beforePath,
    });
  }

  return ops;
}

/**
 * Build set operations for all fields in an entity.
 *
 * Used for new entities where all fields are being set for the first time.
 * Unlike buildDiffOps, this doesn't compare with a before state - it simply
 * generates set ops for all non-undefined fields.
 *
 * @param table - Target sync table name
 * @param recordId - Target record ID
 * @param entity - Entity with all fields to set
 * @returns Array of set operations for all fields
 *
 * @example
 * buildSetOps('trades', 'id-1', { name: 'Trade', amount: 100 })
 * // Returns: [
 * //   { op: 'set', field: 'name', value: 'Trade', ... },
 * //   { op: 'set', field: 'amount', value: 100, ... }
 * // ]
 */
export function buildSetOps(
  table: SyncTableName,
  recordId: string,
  entity: Record<string, unknown>
): LogBatchOperationParams[] {
  const ops: LogBatchOperationParams[] = [];

  // Flatten entity to dot-notation paths
  const flatPaths = flattenToDotNotation(entity);

  for (const [path, value] of Object.entries(flatPaths)) {
    // Skip excluded fields
    if (isExcludedPath(path)) {
      continue;
    }

    // Skip undefined values (not set, not cleared)
    if (value === undefined) {
      continue;
    }

    // Generate set op for this field
    ops.push({
      table,
      recordId,
      op: 'set',
      field: path,
      value,
    });
  }

  return ops;
}

/**
 * Build a delete operation for a record (tombstone).
 *
 * Used when soft-deleting a record. The delete op will be applied via LWW
 * on import, ensuring cross-device deletion propagation.
 *
 * @param table - The sync table name
 * @param recordId - The entity's ID being deleted
 * @returns LogBatchOperationParams for 'delete' operation
 *
 * @example
 * buildDeleteOp('wizard_drafts', 'draft-123')
 * // Returns: { table: 'wizard_drafts', recordId: 'draft-123', op: 'delete' }
 */
export function buildDeleteOp(
  table: SyncTableName,
  recordId: string
): LogBatchOperationParams {
  return {
    table,
    recordId,
    op: 'delete',
  };
}
